import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import styled, { css, useTheme } from "styled-components";
import moment from "moment";
import Label from "~/components-shared/Label";
import Dropdown from "~/components-shared/Dropdown";
import Icon from "~/components-shared/Icon";
import InputText from "~/components-shared/InputText";
import { useTranslation } from "react-i18next";
import { genericColor } from "~/lib/resolvers";
const Wrapper = styled.div`
  ${_ref => {
  let {
    $s,
    theme
  } = _ref;
  return theme.spacing.resolve(theme, $s);
}};
  ${_ref2 => {
  let {
    $fullWidth
  } = _ref2;
  return $fullWidth && "width: 100%";
}};
  ${_ref3 => {
  let {
    $maxWidth
  } = _ref3;
  return $maxWidth && `max-width: ${$maxWidth}`;
}};
`;
const Field = styled.div`
  background-color: ${_ref4 => {
  let {
    theme,
    $color
  } = _ref4;
  return genericColor(theme, $color);
}};
  width: 100%;
  border: none;
  border-radius: ${_ref5 => {
  let {
    theme
  } = _ref5;
  return theme.shape.borderRadius.primary;
}};
  font-weight: ${_ref6 => {
  let {
    theme
  } = _ref6;
  return theme.typography.fontWeight.regular;
}};
  display: flex;
  align-items: center;
  transition: box-shadow 200ms ease-in;
  box-shadow: rgb(255 255 255) 0 0 0 0, transparent 0 0 0 1px;
  ${_ref7 => {
  let {
    $outline,
    $hasError,
    theme
  } = _ref7;
  return theme.shadows.outline(theme, $outline, $hasError ? theme.colors.danger.main : theme.colors.primary.main);
}}
`;
const StyledDropdown = styled(Dropdown)`
  ${_ref8 => {
  let {
    $hasError,
    theme
  } = _ref8;
  return $hasError && css`
      box-shadow: 0 0 0 2px ${theme.colors.danger.main};
    `;
}};
  border-radius: ${_ref9 => {
  let {
    theme
  } = _ref9;
  return theme.shape.borderRadius.primary;
}};
`;
const Error = styled.span`
  font-size: 0.8rem;
  display: inline-block;
  margin-left: 0.5rem;
  color: ${_ref10 => {
  let {
    theme
  } = _ref10;
  return theme.colors.danger.main;
}};
`;
const StyledInputText = styled(InputText)`
  padding-right: 0.1rem;
`;
const SearchInput = styled(InputText)`
  width: 60px;
`;
const InputTime = _ref11 => {
  let {
    label,
    value = "",
    direction = "bottomRight",
    error,
    onChange = () => false,
    required = false,
    disabled = false,
    color = "translucent",
    maxWidth,
    startWith,
    endWith,
    period = 15,
    fullWidth = false,
    s,
    outline,
    children,
    labelProps,
    keepTimezone = false,
    ...rest
  } = _ref11;
  const theme = useTheme();
  const inputDateFormat = "DD/MM/YYYY";
  const {
    t
  } = useTranslation();
  const inputTimeFormat = "hh:mma";
  const [searchValue, setSearchValue] = useState("");
  const defaultInput = value === "" ? "" : moment(value).format(inputTimeFormat);
  const [inputValue, setInputValue] = useState(defaultInput);
  const [dateError, setDateError] = useState(false);
  const start = moment(value || undefined);
  const remainder = period - start.minute() % period;
  const startTime = startWith && moment(startWith, inputTimeFormat).isValid() ? moment(startWith, inputTimeFormat) : start.add(remainder === period ? 0 : remainder, "minutes");
  const endTime = endWith && moment(endWith, inputTimeFormat).isValid() && moment(endWith, inputTimeFormat);
  const times = [startTime.format(inputTimeFormat)];
  const startEndDiffInHours = endTime ? moment.duration(endTime.diff(startTime)).hours() : 24;
  useEffect(() => {
    setInputValue(defaultInput);
  }, [value]);
  new Array(startEndDiffInHours * (60 / period) - (startEndDiffInHours === 24 ? 1 : 0)).fill(1).forEach(() => {
    times.push(startTime.add(period, "minutes").format(inputTimeFormat));
  });
  const filteredTimes = times.filter(item => item.toLowerCase().includes(searchValue.toLowerCase()));
  const handleSearch = query => {
    setSearchValue(query);
  };
  const handleChange = v => {
    setInputValue(v);
    const date = moment(`${moment(value || undefined).format(inputDateFormat)} ${v}`, `${inputDateFormat} ${inputTimeFormat}`, true);
    if (date.isValid()) {
      setDateError(false);
      onChange(date.toISOString(keepTimezone));
      setInputValue(v);
    } else {
      setDateError(true);
    }
  };
  const customHandler = (toggle, isOpen) => /*#__PURE__*/React.createElement(Field, {
    onClick: toggle,
    $outline: isOpen || outline,
    $color: color,
    $hasError: dateError
  }, /*#__PURE__*/React.createElement(StyledInputText, _extends({
    value: inputValue,
    color: "transparent",
    onChange: v => handleChange(v),
    autoComplete: "off",
    outline: false,
    elemAfter: /*#__PURE__*/React.createElement(Icon, {
      src: "chevron",
      scale: 5,
      color: theme.colors.text.secondary,
      rotate: isOpen ? 0 : 180
    }),
    fullWidth: true
  }, rest)));
  return /*#__PURE__*/React.createElement(Wrapper, {
    $s: s,
    $maxWidth: maxWidth,
    $fullWidth: fullWidth
  }, !!label && /*#__PURE__*/React.createElement(Label, _extends({
    required: required,
    disabled: disabled,
    s: {
      mb: 2
    }
  }, labelProps), label), /*#__PURE__*/React.createElement(StyledDropdown, {
    $hasError: dateError,
    direction: direction,
    customHandler: customHandler,
    initValue: inputValue
  }, _ref12 => {
    let {
      isOpen,
      onItemClick,
      selected
    } = _ref12;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      noHover: true,
      sticky: true
    }, /*#__PURE__*/React.createElement(SearchInput, {
      value: searchValue,
      onChange: handleSearch,
      elemBefore: /*#__PURE__*/React.createElement(Icon, {
        src: "search",
        s: {
          mr: 2
        }
      }),
      placeholder: t("common.actions.search"),
      clearButton: true,
      fullWidth: true
    })), filteredTimes.map(time => /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      key: time,
      selected: selected === time,
      onClick: () => {
        onItemClick(time);
        handleChange(time);
      }
    }, time)));
  }), error && /*#__PURE__*/React.createElement(Error, null, error));
};
export default InputTime;